/**
 * Client Hairstyle Analysis API Service
 * Handles saving hairstyle analysis data to client records
 */

import axiosClient from '../api/axiosClient';
import { saveHairstyleImageToFile } from './imageFileManager';

/**
 * Download image as base64 for persistent storage (fallback)
 */
const downloadImageAsBase64 = async (imageUrl) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error downloading image:', error);
    throw new Error('Failed to download generated hairstyle image');
  }
};

/**
 * Save hairstyle analysis data to client record
 */
export const saveClientHairstyleAnalysis = async (clientId, analysisData) => {
  try {
    // Save generated hairstyle image to file system
    let imageFileInfo = null;
    if (analysisData.result?.output) {
      try {
        imageFileInfo = await saveHairstyleImageToFile(
          analysisData.result.output,
          clientId,
          new Date().toISOString()
        );
      } catch (error) {
        console.warn('Could not save hairstyle image to file:', error);
        // Fallback to base64 storage
        try {
          const base64Image = await downloadImageAsBase64(analysisData.result.output);
          imageFileInfo = { base64Image };
        } catch (fallbackError) {
          console.warn('Could not save image in any format:', fallbackError);
        }
      }
    }

    const payload = {
      hairstyle_analysis: {
        face_shape: analysisData.shapeData?.shape,
        face_metrics: analysisData.shapeData?.metrics,
        analysis_prompts: analysisData.shapeData?.prompts,
        analysis_time: analysisData.analysisTime,
        analysis_confidence: analysisData.confidence || 'High',
        generated_hairstyle_url: analysisData.result?.output,
        generated_hairstyle_file_path: imageFileInfo?.filePath || null,
        generated_hairstyle_filename: imageFileInfo?.filename || null,
        generated_hairstyle_image: imageFileInfo?.base64Image || null, // Fallback base64
        generated_hairstyle_prompt: analysisData.result?.promptUsed,
        generation_order_id: analysisData.result?.orderId,
        generation_time: analysisData.generationTime,
        selected_style_index: analysisData.selectedStyleIndex,
        custom_prompt: analysisData.customPrompt,
        analysis_date: new Date().toISOString(),
        original_image_url: analysisData.originalImageUrl,
      }
    };

    const response = await axiosClient.patch(`/admin/clients/${clientId}`, payload);
    
    if (response.data.ok) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Failed to save hairstyle analysis');
    }
  } catch (error) {
    console.error('Error saving hairstyle analysis:', error);
    throw new Error(error.response?.data?.error || 'Failed to save hairstyle analysis');
  }
};

/**
 * Get client hairstyle analysis history
 */
export const getClientHairstyleHistory = async (clientId) => {
  try {
    const response = await axiosClient.get(`/admin/clients/${clientId}`);
    
    if (response.data.ok) {
      // hairstyle_analysis is a single object, not an array
      const hairstyleAnalysis = response.data.data.hairstyle_analysis;
      return hairstyleAnalysis ? [hairstyleAnalysis] : [];
    } else {
      throw new Error(response.data.message || 'Failed to get client hairstyle history');
    }
  } catch (error) {
    console.error('Error getting client hairstyle history:', error);
    throw new Error(error.response?.data?.error || 'Failed to get client hairstyle history');
  }
};

/**
 * Update client with new hairstyle analysis
 */
export const updateClientWithHairstyleAnalysis = async (clientId, analysisData) => {
  try {
    // Skip file upload for now to avoid 500 errors - just store the URL
    let imageFileInfo = null;
    const resolvedFaceShape =
      analysisData.shapeData?.shape ||
      analysisData.result?.gemini_analysis?.face_shape ||
      analysisData.result?.gemini_analysis?.faceShape ||
      analysisData.result?.faceShape ||
      analysisData.result?.shape ||
      null;
    
    // Prepare hairstyle analysis data (optimized to reduce payload size)
    const hairstyleAnalysis = {
      face_shape: resolvedFaceShape,
      analysis_time: analysisData.analysisTime,
      analysis_confidence: analysisData.confidence || 'High',
      generated_hairstyle_url: analysisData.result?.output, // Keep the original URL
      generated_hairstyle_file_path: imageFileInfo?.filePath || null, // File path if saved
      generated_hairstyle_filename: imageFileInfo?.filename || null, // Filename if saved
      generated_hairstyle_prompt: analysisData.result?.promptUsed,
      generation_order_id: analysisData.result?.orderId,
      generation_time: analysisData.generationTime,
      selected_style_index: analysisData.selectedStyleIndex,
      custom_prompt: analysisData.customPrompt,
      analysis_date: new Date().toISOString(),
      original_image_url: analysisData.originalImageUrl,
    };

    // Update client with hairstyle analysis (lightweight payload)
    const updatePayload = {
      hairstyle_analysis: hairstyleAnalysis,
      last_hairstyle_analysis: new Date().toISOString(),
    };
    
    // Add timeout and retry configuration to prevent hanging requests
    const response = await axiosClient.patch(`/admin/clients/${clientId}`, updatePayload, {
      timeout: 60000, // 60 second timeout for large payloads
      headers: {
        'Content-Type': 'application/json',
      },
      maxContentLength: 10 * 1024 * 1024, // 10MB max content length
      maxBodyLength: 10 * 1024 * 1024, // 10MB max body length
    });
    
    if (response.data.ok) {
      // Also create a scan record for the AI Analysis modal
      try {
        await createScanRecord(clientId, analysisData, hairstyleAnalysis);
      } catch (scanError) {
        // Don't fail the whole operation if scan record creation fails
      }
      
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Failed to update client with hairstyle analysis');
    }
  } catch (error) {
    console.error('❌ Error updating client with hairstyle analysis:', error);
    throw new Error(error.response?.data?.error || 'Failed to update client with hairstyle analysis');
  }
};

/**
 * Create a scan record for the AI Analysis modal
 */
const createScanRecord = async (clientId, analysisData, hairstyleAnalysis) => {
  try {
    // Get client info to extract client name
    const clientResponse = await axiosClient.get(`/admin/clients/${clientId}`);
    if (!clientResponse.data.ok) {
      throw new Error('Failed to get client info');
    }
    
    const clientName = clientResponse.data.data.name;
    
    // Get current user info for barber_id
    const userResponse = await axiosClient.get('/auth/me');
    const barberId = userResponse.data.user?.id;
    
    if (!barberId) throw new Error('Failed to get barber ID');
    
    // Build multipart form to upload ORIGINAL photo and link the generated URL
    // Convert the original image URL (likely a data URL) to a Blob
    const originalUrl = analysisData.originalImageUrl;
    if (!originalUrl) {
      throw new Error('Missing original image for scan creation');
    }

    const blob = await (await fetch(originalUrl)).blob();
    const file = new File([blob], 'original.jpg', { type: blob.type || 'image/jpeg' });

    const formData = new FormData();
    formData.append('image', file);
    formData.append('client_name', clientName);
    formData.append('barber_id', barberId);

    // Pass the generated hairstyle URL so backend stores it in styledImages
    if (analysisData.result?.output) {
      const resolvedFaceShape =
        analysisData.shapeData?.shape ||
        analysisData.result?.gemini_analysis?.face_shape ||
        analysisData.result?.gemini_analysis?.faceShape ||
        analysisData.result?.faceShape ||
        analysisData.result?.shape ||
        hairstyleAnalysis?.face_shape ||
        null;
      const resolvedStyleName =
        analysisData.result?.resolvedStyleName ||
        analysisData.result?.selectedSuggestionName ||
        analysisData.result?.style ||
        '';
      const rawHairProfile =
        analysisData.result?.gemini_analysis?.hair_profile ||
        analysisData.result?.gemini_analysis?.hairProfile ||
        analysisData.shapeData?.hair_profile ||
        analysisData.shapeData?.hairProfile ||
        null;

      formData.append('styled_image_url', analysisData.result.output);
      // Pass metadata so details appear in modal
      if (resolvedFaceShape) formData.append('face_shape', resolvedFaceShape);
      if (analysisData.result?.promptUsed || analysisData.customPrompt) formData.append('prompt_used', analysisData.result?.promptUsed || analysisData.customPrompt);
      if (resolvedStyleName) {
        formData.append('resolved_style_name', resolvedStyleName);
        formData.append('haircut', resolvedStyleName);
      }
      if (rawHairProfile && typeof rawHairProfile === 'object') {
        formData.append('hair_profile', JSON.stringify(rawHairProfile));
      }
      const providerValue = analysisData.result?.provider || analysisData.result?.generationProvider;
      if (providerValue) formData.append('provider', providerValue);
      formData.append('method', 'ai_generation');
      const overlayPoints = analysisData.shapeData?.overlayPoints || analysisData.shapeData?.overlay_points;
      if (overlayPoints) formData.append('overlay_points', JSON.stringify(overlayPoints));
    }

    const uploadResponse = await axiosClient.post('/scans/upload', formData, {
      // Let the browser set the correct multipart boundary
      timeout: 30000,
    });

    if (!uploadResponse.data.scanId) throw new Error('No scan ID returned from upload');
  } catch (error) {
    throw error;
  }
};

const clientHairstyleApi = {
  saveClientHairstyleAnalysis,
  getClientHairstyleHistory,
  updateClientWithHairstyleAnalysis,
};

export default clientHairstyleApi;
